<template>
  <v-container fluid class="exp-page">
    <section class="exp-container">
      <!-- Month Filter & Overview Header Bar -->
      <app-card-title @filter="filter" />

      <!-- KPI Summary Cards (Income, Expense, Net Profit) -->
      <app-expense-card @addNewExpense="addNewExpense" :expense="summary" />

      <!-- Main Records Panel -->
      <v-card class="exp-main-card" flat>
        <v-card-text class="pa-4">
          <!-- Table Toolbar: Title, Count, Type Filter Tabs, Search & Action Buttons -->
          <div class="d-flex align-center flex-wrap exp-toolbar mb-4">
            <div class="d-flex align-center exp-title-group mr-3">
              <span class="exp-title-accent mr-2"></span>
              <div>
                <h1 class="exp-page-title mb-0">{{ $t("expense") }}</h1>
                <span class="exp-item-count" v-if="filteredExpenseItems">
                  {{ filteredExpenseItems.length }} {{ $t("items") }}
                </span>
              </div>
            </div>

            <!-- Type Filter Tabs (All / Income / Expense) -->
            <div class="exp-type-tabs mr-auto">
              <div
                class="exp-tab-item"
                :class="{ 'exp-tab-item--active': activeTypeFilter === 'all' }"
                @click="activeTypeFilter = 'all'"
              >
                {{ $t("all") }}
                <span class="exp-tab-badge">{{ totalCount }}</span>
              </div>
              <div
                class="exp-tab-item exp-tab-item--income"
                :class="{ 'exp-tab-item--active': activeTypeFilter === 'income' }"
                @click="activeTypeFilter = 'income'"
              >
                <v-icon size="12" class="mr-1" :color="activeTypeFilter === 'income' ? 'success' : 'grey'">
                  fal fa-arrow-down
                </v-icon>
                {{ $t("income") }}
                <span class="exp-tab-badge exp-tab-badge--income">{{ incomeCount }}</span>
              </div>
              <div
                class="exp-tab-item exp-tab-item--expense"
                :class="{ 'exp-tab-item--active': activeTypeFilter === 'expense' }"
                @click="activeTypeFilter = 'expense'"
              >
                <v-icon size="12" class="mr-1" :color="activeTypeFilter === 'expense' ? 'error' : 'grey'">
                  fal fa-arrow-up
                </v-icon>
                {{ $t("expense") }}
                <span class="exp-tab-badge exp-tab-badge--expense">{{ expenseCount }}</span>
              </div>
            </div>

            <!-- Search Field & Actions -->
            <div class="d-flex align-center flex-wrap exp-toolbar-actions mt-2 mt-sm-0">
              <div class="exp-search-box mr-2">
                <TextSearch
                  v-model="search"
                  @search="fetchItem()"
                  @onClear="
                    search = '';
                    fetchItem();
                  "
                />
              </div>

              <!-- Category Link Button -->
              <v-btn
                outlined
                depressed
                class="exp-btn-secondary mr-2"
                to="/expense-category"
              >
                <v-icon left small>fal fa-tags</v-icon>
                {{ $t("expense_category") }}
              </v-btn>

              <!-- New Record Action Button -->
              <v-btn
                depressed
                class="exp-btn-primary"
                @click="newItem"
              >
                <v-icon left small>fal fa-plus</v-icon>
                {{ $t("new_data") }}
              </v-btn>
            </div>
          </div>

          <!-- Expense Data Table -->
          <v-data-table
            :headers="headers"
            :items="filteredExpenseItems"
            :loading="isLoading"
            :loading-text="$t('loading')"
            :no-data-text="$t('no_data')"
            hide-default-footer
            class="exp-table elevation-0"
          >
            <!-- Index Column -->
            <template v-slot:item.index="{ index }">
              <span class="exp-col-index">{{ index + 1 }}</span>
            </template>

            <!-- Name / Title Column with Category Subtext -->
            <template v-slot:item.name="{ item }">
              <div class="d-flex flex-column py-1">
                <span class="exp-item-name font-weight-medium">{{ item.name }}</span>
                <span v-if="item.category && item.category.name" class="exp-item-category-subtext">
                  <v-icon size="11" class="mr-1">fal fa-tag</v-icon>{{ item.category.name }}
                </span>
              </div>
            </template>

            <!-- Price Column formatted with visual sign & currency -->
            <template v-slot:item.price="{ item }">
              <span
                class="exp-price-badge font-weight-bold"
                :class="item.type === 'income' ? 'exp-price-badge--income' : 'exp-price-badge--expense'"
              >
                {{ item.type === 'income' ? '+' : '-' }}{{ item.price | NumberFormat }}
                <span class="exp-price-currency">LAK</span>
              </span>
            </template>

            <!-- Type Column (Pill Tag) -->
            <template v-slot:item.typeExpense="{ item }">
              <v-chip
                x-small
                label
                class="font-weight-bold text-uppercase exp-type-chip"
                :class="item.type === 'income' ? 'exp-type-chip--income' : 'exp-type-chip--expense'"
              >
                <v-icon x-small class="mr-1">
                  {{ item.type === 'income' ? 'fal fa-arrow-down' : 'fal fa-arrow-up' }}
                </v-icon>
                {{ $t(item.type) }}
              </v-chip>
            </template>

            <!-- Category Column -->
            <template v-slot:item.category.name="{ item }">
              <v-chip
                v-if="item.category && item.category.name"
                x-small
                outlined
                color="#64748b"
                class="exp-category-pill"
              >
                {{ item.category.name }}
              </v-chip>
              <span v-else class="text--secondary text-caption">-</span>
            </template>

            <!-- Created At Date Column -->
            <template v-slot:item.created_at="{ item }">
              <span class="exp-date-cell">
                <v-icon size="12" class="mr-1" color="#94a3b8">fal fa-clock</v-icon>
                {{ item.created_at | moment }}
              </span>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-end exp-actions-wrap">
                <!-- View Detail -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      class="exp-action-btn mr-1"
                      @click="viewItem(item)"
                    >
                      <v-icon size="14" color="#64748b">fal fa-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("view_details") }}</span>
                </v-tooltip>

                <!-- Edit -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      class="exp-action-btn mr-1"
                      @click="editItem(item)"
                    >
                      <v-icon size="14" color="primary">fal fa-edit</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("edit") }}</span>
                </v-tooltip>

                <!-- Delete -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      class="exp-action-btn exp-action-btn--delete"
                      @click="deleteItem(item)"
                    >
                      <v-icon size="14" color="error">fal fa-trash-alt</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("delete") }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>

          <!-- Custom Pagination Footer -->
          <div
            v-if="expensePagination && expensePagination.last_page > 1"
            class="d-flex align-center justify-space-between flex-wrap mt-4 pt-3 border-top exp-pagination"
          >
            <span class="exp-page-info">
              {{ $t("showing") || "Showing" }}
              {{ (expensePagination.current_page - 1) * expensePagination.per_page + 1 }} -
              {{ Math.min(expensePagination.current_page * expensePagination.per_page, expensePagination.total) }}
              {{ $t("of") || "of" }} {{ expensePagination.total }}
            </span>
            <div class="d-flex align-center exp-page-nav">
              <v-btn
                icon
                small
                outlined
                :disabled="expensePagination.current_page <= 1"
                class="exp-nav-btn mr-1"
                @click="goToPage(expensePagination.current_page - 1)"
              >
                <v-icon small>fal fa-chevron-left</v-icon>
              </v-btn>
              <span class="exp-page-current mx-2">
                {{ expensePagination.current_page }} / {{ expensePagination.last_page }}
              </span>
              <v-btn
                icon
                small
                outlined
                :disabled="expensePagination.current_page >= expensePagination.last_page"
                class="exp-nav-btn ml-1"
                @click="goToPage(expensePagination.current_page + 1)"
              >
                <v-icon small>fal fa-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- View Item Modal (Redesigned with receipts-style card) -->
      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="description"
        width="460"
        @submit="submitForm"
      >
        <div slot="activator" class="pa-2">
          <div
            class="exp-view-header pa-4 mb-3 rounded-lg text-center"
            :class="form.type === 'income' ? 'exp-view-header--income' : 'exp-view-header--expense'"
          >
            <div class="exp-view-type-tag mb-1">
              <v-chip
                small
                label
                class="font-weight-bold"
                :color="form.type === 'income' ? 'success' : 'error'"
                dark
              >
                {{ $t(form.type || "expense") }}
              </v-chip>
            </div>
            <div class="exp-view-amount font-weight-bold mt-2">
              {{ form.type === 'income' ? '+' : '-' }}{{ (form.price || 0) | NumberFormat }}
              <span class="exp-view-currency">LAK</span>
            </div>
            <div class="exp-view-title mt-1">{{ form.name }}</div>
          </div>

          <v-card outlined class="exp-view-details rounded-lg" elevation="0">
            <v-list dense class="pa-0">
              <v-list-item class="exp-detail-row">
                <v-list-item-content class="text-caption grey--text">{{ $t("id") }}</v-list-item-content>
                <v-list-item-action class="font-weight-medium text-caption">#{{ form.id }}</v-list-item-action>
              </v-list-item>
              <v-divider class="my-0" />
              <v-list-item class="exp-detail-row">
                <v-list-item-content class="text-caption grey--text">{{ $t("category") }}</v-list-item-content>
                <v-list-item-action class="font-weight-medium text-caption">
                  {{ form.category ? form.category.name : '-' }}
                </v-list-item-action>
              </v-list-item>
              <v-divider class="my-0" />
              <v-list-item class="exp-detail-row">
                <v-list-item-content class="text-caption grey--text">{{ $t("created_at") }}</v-list-item-content>
                <v-list-item-action class="font-weight-medium text-caption">
                  {{ form.created_at | moment }}
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </div>
      </app-custom-dialog>

      <!-- Add / Edit Item Modal -->
      <app-custom-dialog
        v-model="dialogAdd"
        :footer="true"
        :title="titleStatus"
        :submit-text="update ? 'save' : 'add'"
        :submit-loading="submitting"
        width="500"
        @submit="submitForm"
      >
        <div slot="activator" class="pt-2">
          <!-- Type Switch Toggle -->
          <div class="mb-4">
            <label class="exp-form-label">{{ $t("type") }}</label>
            <div class="d-flex exp-type-switch mt-1">
              <div
                class="exp-switch-btn exp-switch-btn--income"
                :class="{ 'exp-switch-btn--active': form.type === 'income' || (form.type && form.type.value === 'income') }"
                @click="setType('income')"
              >
                <v-icon small class="mr-1">fal fa-arrow-down</v-icon>
                {{ $t("income") }}
              </div>
              <div
                class="exp-switch-btn exp-switch-btn--expense"
                :class="{ 'exp-switch-btn--active': form.type === 'expense' || (form.type && form.type.value === 'expense') }"
                @click="setType('expense')"
              >
                <v-icon small class="mr-1">fal fa-arrow-up</v-icon>
                {{ $t("expense") }}
              </div>
            </div>
            <div v-if="typeError && typeError.length" class="red--text text-caption mt-1">
              {{ typeError[0] }}
            </div>
          </div>

          <!-- Title / Name Input -->
          <div class="mb-2">
            <TTextField
              label="name"
              :errorMessages="nameError"
              v-model="form.name"
              @input="$v.form.name.$touch()"
              @blur="$v.form.name.$touch()"
            />
          </div>

          <!-- Price Input -->
          <div class="mb-2">
            <CurrencyInput
              label="price"
              :errorMessages="priceError"
              v-model="form.price"
              @input="$v.form.price.$touch()"
              @blur="$v.form.price.$touch()"
            />
          </div>

          <!-- Category Select -->
          <div class="mb-2">
            <TextSelect
              :errorMessages="category_idError"
              label="category"
              v-model="form.category_id"
              @input="$v.form.category_id.$touch()"
              @blur="$v.form.category_id.$touch()"
              itemValue="id"
              text="name"
              :items="category"
            />
          </div>
        </div>
      </app-custom-dialog>

      <!-- AppLoading Overlay -->
      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { expenseHead } from "@/utils/head";
import AppExpenseCard from "@/components/expense/card";
import AppCardTitle from "@/components/expense/card-title";
import TTextField from "@/components/global/TextField";
import TextSelect from "@/components/global/TextSelect";
import { myMixin } from "@/mixin/main";
import { expenseValidate } from "@/validations/shop";
import { success, confirm, toast } from "@/controllers/alert";
import CurrencyInput from "@/components/CurrencyInput";

const defaultExpenseForm = () => ({
  name: "",
  price: "",
  category_id: "",
  category: {},
  type: "expense",
  desc: "",
});

export default {
  mixins: [myMixin, expenseValidate],
  data: () => ({
    options: {
      precision: 0,
    },
    update: false,
    dialogAdd: false,
    dialog: false,
    overlay: false,
    isLoading: false,
    submitting: false,
    search: "",
    summary: [],
    activeTypeFilter: "all",
    items: [
      {
        name: "ລາຍຮັບ",
        value: "income",
      },
      {
        name: "ລາຍຈ່າຍ",
        value: "expense",
      },
    ],
    form: defaultExpenseForm(),
  }),
  async created() {
    await this.fetchItem();
    await this.getSummary();
    await this.fetchCategory();
  },
  components: {
    CurrencyInput,
    TextSelect,
    TTextField,
    AppExpenseCard,
    AppCardTitle,
  },
  computed: {
    ...mapGetters({
      expense: "index/expense",
      category: "index/category",
    }),
    titleStatus() {
      return this.update ? "update" : "new_data";
    },
    headers() {
      return expenseHead(this);
    },
    rawExpenseItems() {
      if (!this.expense) return [];
      if (Array.isArray(this.expense)) return this.expense;
      if (this.expense.data && Array.isArray(this.expense.data)) {
        return this.expense.data;
      }
      return [];
    },
    filteredExpenseItems() {
      if (this.activeTypeFilter === "all") {
        return this.rawExpenseItems;
      }
      return this.rawExpenseItems.filter(
        (item) => item.type === this.activeTypeFilter
      );
    },
    totalCount() {
      return this.rawExpenseItems.length;
    },
    incomeCount() {
      return this.rawExpenseItems.filter((i) => i.type === "income").length;
    },
    expenseCount() {
      return this.rawExpenseItems.filter((i) => i.type === "expense").length;
    },
    expensePagination() {
      if (this.expense && this.expense.current_page) {
        return {
          current_page: this.expense.current_page,
          last_page: this.expense.last_page,
          per_page: this.expense.per_page,
          total: this.expense.total,
        };
      }
      return null;
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    async fetchItem(page = 1) {
      this.isLoading = true;
      const body = {
        that: this,
        url: `expense_paginate?page=${page}&q=${this.search}`,
        commit: "SET_EXPENSE",
      };
      await this.fetch(body);
      this.isLoading = false;
    },
    async goToPage(page) {
      await this.fetchItem(page);
    },
    async fetchCategory() {
      const body = {
        that: this,
        url: "expense_category",
        commit: "SET_CATEGORY",
      };
      await this.fetch(body);
    },

    async newItem() {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.form = defaultExpenseForm();
      this.form.type = "expense";
      await this.fetchCategory();
      this.update = false;
      this.dialogAdd = true;
    },
    editItem(item) {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.update = true;
      this.form = {
        ...defaultExpenseForm(),
        ...item,
      };
      if (item.category && item.category.id) {
        this.form.category_id = item.category.id;
      }
      this.dialogAdd = true;
    },
    async deleteItem({ id }) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        try {
          const { data } = await this.$http.delete(`expense/${id}`);
          if (data && data.status) {
            await this.fetchItem();
            await this.getSummary();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("error_occurred") ||
            "Error";
          if (this.$toast) {
            this.$toast.error(message);
          } else {
            await toast(this, message, "error");
          }
        }
      }
    },
    viewItem(item) {
      this.form = {
        ...defaultExpenseForm(),
        ...item,
      };
      this.dialog = true;
    },
    async addNewExpense(type) {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.form = defaultExpenseForm();
      this.form.type = type;
      await this.fetchCategory();
      this.update = false;
      this.dialogAdd = true;
    },
    setType(type) {
      this.form.type = type;
      this.$v.form.type.$touch();
    },
    async submitForm() {
      try {
        this.$v.form.$touch();
        if (this.$v.form.$invalid) {
          return;
        }

        this.submitting = true;
        const categoryId =
          typeof this.form.category_id === "object" && this.form.category_id !== null
            ? this.form.category_id.id
            : this.form.category_id;
        const typeVal =
          typeof this.form.type === "object" && this.form.type !== null
            ? this.form.type.value
            : this.form.type;

        const payload = {
          ...this.form,
          category_id: categoryId,
          type: typeVal,
        };

        let result;
        if (this.update) {
          result = await this.$http.put(`expense/${this.form.id}`, payload);
        } else {
          result = await this.$http.post("expense", payload);
        }
        if (result && result.data && result.data.status) {
          this.dialogAdd = false;
          await this.fetchItem();
          await this.getSummary();
          await success(this, result.data.message);
        } else {
          await toast(this, result?.data?.message || "error_occurred", "error");
        }
      } catch (error) {
        console.error("error submitting expense", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("error_occurred") ||
          "Error";
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          await toast(this, message, "error");
        }
      } finally {
        this.submitting = false;
      }
    },
    async filter(val) {
      this.overlay = true;
      await this.getSummary(val);
      this.overlay = false;
    },
    async getSummary(val) {
      let url = `expense_summary`;
      if (val) {
        url = `expense_summary?q=${val}`;
      }
      const { data } = await this.$http.get(`${url}`);
      if (data.status) {
        this.summary = data.data;
      }
    },
  },
};
</script>

<style scoped>
.exp-page {
  background: #f6f6fa;
  min-height: calc(100vh - 64px);
}

.exp-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Main Table Panel */
.exp-main-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}

/* Toolbar Header */
.exp-toolbar {
  gap: 12px;
}

.exp-title-accent {
  width: 4px;
  height: 24px;
  background: #5b39c9;
  border-radius: 4px;
}

.exp-page-title {
  font-size: 18px;
  font-weight: 800;
  color: #141021;
  line-height: 1.2;
}

.exp-item-count {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

/* Type Filter Tabs */
.exp-type-tabs {
  display: inline-flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 3px;
  gap: 4px;
}

.exp-tab-item {
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  user-select: none;
}

.exp-tab-item:hover {
  color: #141021;
}

.exp-tab-item--active {
  background: #ffffff !important;
  color: #5b39c9 !important;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.exp-tab-item--income.exp-tab-item--active {
  color: #059669 !important;
}

.exp-tab-item--expense.exp-tab-item--active {
  color: #dc2626 !important;
}

.exp-tab-badge {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  background: rgba(148, 163, 184, 0.2);
  color: inherit;
}

.exp-tab-badge--income {
  background: rgba(16, 185, 129, 0.15);
}

.exp-tab-badge--expense {
  background: rgba(239, 68, 68, 0.15);
}

/* Toolbar Buttons */
.exp-search-box {
  min-width: 200px;
  max-width: 260px;
}

.exp-btn-secondary {
  height: 38px !important;
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
  color: #475569 !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.exp-btn-secondary:hover {
  border-color: #5b39c9 !important;
  color: #5b39c9 !important;
}

.exp-btn-primary {
  height: 38px !important;
  border-radius: 8px !important;
  background: #5b39c9 !important;
  color: #ffffff !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 6px rgba(91, 57, 201, 0.25) !important;
}

.exp-btn-primary:hover {
  background: #4a2cb5 !important;
}

/* Table Styling */
.exp-table ::v-deep th {
  background: #f8fafc !important;
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 10px 16px !important;
}

.exp-table ::v-deep td {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 12px 16px !important;
  font-size: 13px !important;
  color: #141021;
}

.exp-table ::v-deep tr:hover td {
  background: #faf8ff !important;
}

.exp-col-index {
  color: #94a3b8;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.exp-item-name {
  font-size: 13.5px;
  color: #141021;
}

.exp-item-category-subtext {
  font-size: 11px;
  color: #94a3b8;
}

.exp-price-badge {
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

.exp-price-badge--income {
  color: #059669;
}

.exp-price-badge--expense {
  color: #dc2626;
}

.exp-price-currency {
  font-size: 10.5px;
  font-weight: 600;
  color: #94a3b8;
}

.exp-type-chip {
  font-size: 10.5px !important;
  height: 20px !important;
  border-radius: 6px !important;
}

.exp-type-chip--income {
  background: rgba(16, 185, 129, 0.12) !important;
  color: #059669 !important;
}

.exp-type-chip--expense {
  background: rgba(239, 68, 68, 0.12) !important;
  color: #dc2626 !important;
}

.exp-category-pill {
  border-radius: 6px !important;
  font-size: 11px !important;
  height: 20px !important;
}

.exp-date-cell {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Action Buttons in Row */
.exp-actions-wrap {
  gap: 2px;
}

.exp-action-btn {
  width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
}

.exp-action-btn:hover {
  background: #ede9fe !important;
  border-color: #5b39c9 !important;
}

.exp-action-btn--delete:hover {
  background: #fee2e2 !important;
  border-color: #ef4444 !important;
}

/* Pagination */
.exp-page-info {
  font-size: 12px;
  color: #64748b;
}

.exp-nav-btn {
  border-radius: 6px !important;
  border-color: #e2e8f0 !important;
  width: 28px !important;
  height: 28px !important;
}

.exp-page-current {
  font-size: 12px;
  font-weight: 600;
  color: #141021;
}

/* View Modal Styling */
.exp-view-header {
  border: 1px solid transparent;
}

.exp-view-header--income {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.exp-view-header--expense {
  background: #fef2f2;
  border-color: #fecaca;
}

.exp-view-amount {
  font-size: 26px;
  line-height: 1.1;
  color: #141021;
}

.exp-view-currency {
  font-size: 13px;
  color: #64748b;
}

.exp-view-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.exp-view-details {
  border-color: #e2e8f0 !important;
}

.exp-detail-row {
  min-height: 40px !important;
}

/* Form Styling */
.exp-form-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.exp-type-switch {
  gap: 10px;
}

.exp-switch-btn {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  user-select: none;
}

.exp-switch-btn--income {
  color: #059669;
}

.exp-switch-btn--expense {
  color: #dc2626;
}

.exp-switch-btn--income.exp-switch-btn--active {
  border-color: #10b981;
  background: #ecfdf5;
}

.exp-switch-btn--expense.exp-switch-btn--active {
  border-color: #ef4444;
  background: #fef2f2;
}

/* =========================================
   DARK MODE OVERRIDES
========================================= */
.theme--dark.exp-page,
.theme--dark .exp-page {
  background: transparent !important;
}

.theme--dark .exp-main-card {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .exp-page-title {
  color: #ffffff;
}

.theme--dark .exp-item-count {
  color: #94a3b8;
}

.theme--dark .exp-type-tabs {
  background: #161827;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .exp-tab-item {
  color: #94a3b8;
}

.theme--dark .exp-tab-item:hover {
  color: #ffffff;
}

.theme--dark .exp-tab-item--active {
  background: rgba(154, 140, 240, 0.18);
  color: #c4b5fd !important;
  box-shadow: none;
}

.theme--dark .exp-tab-item--income.exp-tab-item--active {
  background: rgba(16, 185, 129, 0.16);
  color: #34d399 !important;
}

.theme--dark .exp-tab-item--expense.exp-tab-item--active {
  background: rgba(239, 68, 68, 0.16);
  color: #f87171 !important;
}

.theme--dark .exp-btn-secondary {
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #cbd5e1 !important;
}

.theme--dark .exp-btn-secondary:hover {
  border-color: #818cf8 !important;
  color: #c4b5fd !important;
}

.theme--dark .exp-table ::v-deep th {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  color: #94a3b8 !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .exp-table ::v-deep td {
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
  color: #f1f5f9 !important;
}

.theme--dark .exp-table ::v-deep tr:hover td {
  background: rgba(255, 255, 255, 0.03) !important;
}

.theme--dark .exp-item-name {
  color: #ffffff;
}

.theme--dark .exp-price-badge--income {
  color: #34d399;
}

.theme--dark .exp-price-badge--expense {
  color: #f87171;
}

.theme--dark .exp-type-chip--income {
  background: rgba(16, 185, 129, 0.16) !important;
  color: #34d399 !important;
}

.theme--dark .exp-type-chip--expense {
  background: rgba(239, 68, 68, 0.16) !important;
  color: #f87171 !important;
}

.theme--dark .exp-action-btn {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.theme--dark .exp-action-btn:hover {
  background: rgba(129, 140, 248, 0.2) !important;
  border-color: #818cf8 !important;
}

.theme--dark .exp-action-btn--delete:hover {
  background: rgba(239, 68, 68, 0.2) !important;
  border-color: #ef4444 !important;
}

.theme--dark .exp-nav-btn {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .exp-page-current {
  color: #ffffff;
}

.theme--dark .exp-view-header--income {
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.3);
}

.theme--dark .exp-view-header--expense {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.3);
}

.theme--dark .exp-view-amount {
  color: #ffffff;
}

.theme--dark .exp-view-title {
  color: #cbd5e1;
}

.theme--dark .exp-view-details {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .exp-form-label {
  color: #cbd5e1;
}

.theme--dark .exp-switch-btn {
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .exp-switch-btn--income {
  color: #34d399;
}

.theme--dark .exp-switch-btn--expense {
  color: #f87171;
}

.theme--dark .exp-switch-btn--income.exp-switch-btn--active {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.15);
}

.theme--dark .exp-switch-btn--expense.exp-switch-btn--active {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}
</style>
